/**
 * Close one punch-list item: it was fixed, or it will not be and that is
 * agreed.
 *
 * Two exits rather than one, because they mean opposite things and the audit
 * trail has to keep them apart. RESOLVE says the work happened. WAIVE says it
 * will not, deliberately - a partial roof, a one-time exception, a contractor
 * doing something different instead. Both close the item, and closing it is
 * what unblocks the claim from settling.
 *
 * Resolving needs proof: a note of what was done, photos of the finished work,
 * or both (docs/inspections-platform.md, section 10). The photos become repair
 * photos in the inspection's evidence.
 *
 * A waiver requires a reason. A year later, a waiver with no stated reason is
 * indistinguishable from a mistake, and the person reading it will be trying
 * to work out why a claim settled with work outstanding. The backend enforces
 * both rules too - the fields are not the guard, they are the prompt.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type { PunchListItem, PunchListWorkItem } from '@/admin/hooks/use-inspections'
import { useUploadSubjectEvidence } from '@/admin/hooks/use-subject-attachments'

type CloseAction = 'resolve' | 'waive'

export function ClosePunchListItemDialog({
  item,
  inspectionUuid,
  open,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  /** The item, with where it came from when it is shown away from its inspection. */
  item: (PunchListItem & Partial<Pick<PunchListWorkItem, 'inspection_code' | 'contractor_name'>>) | null
  /** The inspection the item belongs to; proof photos are uploaded to it. */
  inspectionUuid: string | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (action: CloseAction, text: string, photos: string[]) => void | Promise<void>
  isPending: boolean
}) {
  const { t } = useTranslation()
  const [action, setAction] = useState<CloseAction>('resolve')
  const upload = useUploadSubjectEvidence('inspection', inspectionUuid, 'INSPECTION_EVIDENCE')
  const proofRequired = t(
    'inspections.punchList.proofRequired',
    'Add a note or a photo to close this item.',
  )

  const fields: FormField[] = [
    {
      type: 'textarea',
      name: 'text',
      label:
        action === 'resolve'
          ? t('inspections.punchList.notesLabel', 'What was done')
          : t('inspections.punchList.reasonLabel', 'Why it is being waived'),
      value: '',
      placeholder:
        action === 'resolve'
          ? t(
              'inspections.punchList.notesPlaceholder',
              'What the contractor did to close this out.',
            )
          : t(
              'inspections.punchList.reasonPlaceholder',
              'Required - e.g. partial roof, alternative remediation agreed.',
            ),
      // Required only for a waiver: a resolution needs a note or a photo,
      // checked on submit.
      ...(action === 'waive'
        ? {
            required: t(
              'inspections.punchList.reasonRequired',
              'A reason is required to waive an item.',
            ),
            validationSchema: z
              .string()
              .trim()
              .min(
                1,
                t(
                  'inspections.punchList.reasonRequired',
                  'A reason is required to waive an item.',
                ),
              ),
          }
        : { validationSchema: z.string().optional() }),
    },
    ...(action === 'resolve'
      ? [
          {
            type: 'picture',
            name: 'photos',
            label: t('inspections.punchList.photosLabel', 'Photos'),
            hint: t('inspections.punchList.photosHint', 'Take or choose photos of the finished work.'),
            value: [],
            multiple: true,
            useCase: 'INSPECTION_EVIDENCE',
            // A photo is proof enough, so the note stops asking for one.
            onChange: (photos, ctx) => {
              if (Array.isArray(photos) && photos.length > 0) ctx?.setFieldError('text', undefined)
            },
          } satisfies FormField,
        ]
      : []),
  ]

  const { Form, FormFooter } = useForm(
    {
      name: 'close-punch-list-item',
      submit: 'footer',
      fields,
      // A proof photo is uploaded to the inspection, labelled with the item.
      uploadFile: async ({ file }) => {
        const uploaded = await upload.mutateAsync({
          file,
          category: item?.description.split('\n')[0] ?? '',
        })
        return {
          uuid: uploaded.uuid,
          file_name: uploaded.file_name,
          file_url: uploaded.file_url,
          mime_type: uploaded.mime_type,
        }
      },
    },
    async (values, formCtx) => {
      const v = values as { text?: string; photos?: Array<{ uuid?: string }> }
      const text = String(v.text ?? '')
      const photos = (v.photos ?? []).map((photo) => photo.uuid ?? '').filter(Boolean)
      if (action === 'resolve' && !text.trim() && photos.length === 0) {
        formCtx.setFieldError('text', proofRequired)
        return
      }
      await onSubmit(action, text, photos)
    },
    // `action` swaps the field's label, placeholder and validation, so it has
    // to be here by name. No `type:'custom'` render closure in this form, so
    // there is no stale-closure trap - but the same rule applies.
    [open, item?.uuid, action, proofRequired],
  )

  if (!item) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('inspections.punchList.closeTitle', 'Close punch-list item')}
          </DialogTitle>
          <DialogDescription className="whitespace-pre-line">
            {item.description}
            {item.inspection_code ? ` · ${item.inspection_code}` : ''}
            {item.contractor_name ? ` · ${item.contractor_name}` : ''}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={action}
          onValueChange={(v: string) => setAction(v as CloseAction)}
        >
          <TabsList variant="line">
            <TabsTrigger value="resolve">
              {t('inspections.punchList.actionResolve', 'Resolved')}
            </TabsTrigger>
            <TabsTrigger value="waive">
              {t('inspections.punchList.actionWaive', 'Waive')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <p className="text-[12px] text-[var(--ink-3)]">
          {action === 'resolve'
            ? t(
                'inspections.punchList.resolveHint',
                'The work was done. This clears the item and unblocks the claim.',
              )
            : t(
                'inspections.punchList.waiveHint',
                'The work will not be done, and that is agreed. This also unblocks the claim, so the reason is what explains it later.',
              )}
        </p>

        <Form className="flex flex-col gap-4 py-1" />

        {FormFooter && (
          <FormFooter
            onCancel={() => onOpenChange(false)}
            // Stays enabled on an empty required field - clicking validates
            // and highlights it, per the house rule.
            submitText={
              isPending
                ? t('inspections.punchList.closing', 'Closing…')
                : action === 'resolve'
                  ? t('inspections.punchList.actionResolve', 'Resolved')
                  : t('inspections.punchList.actionWaive', 'Waive')
            }
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
